import { styled } from '@mui/material/styles';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import type { DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/en-au';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.locale('en-au');

interface CustomTimePickerProps extends Omit<TimePickerProps<Dayjs>, 'value' | 'onChange' | 'defaultValue'> {
  value?: Dayjs | string | null;
  defaultValue?: Dayjs | string | null;
  onChange: (value: Dayjs | null) => void;
  fullWidth?: boolean;
}

export const CustomTimePicker = ({
  value,
  defaultValue,
  onChange,
  label = null,
  views = ['hours', 'minutes'],
  timeSteps = { hours: 1, minutes: 1 },
  format = 'hh:mm a',
  disableFuture = false,
  fullWidth = false,
  ...others
}: CustomTimePickerProps) => (
  <TimePicker
    value={value ? dayjs(value) : null}
    defaultValue={defaultValue ? dayjs(defaultValue) : null}
    onChange={onChange}
    label={label}
    views={views}
    timeSteps={timeSteps}
    format={format}
    disableFuture={disableFuture}
    slotProps={{ textField: { fullWidth } }}
    {...others}
  />
);

interface CustomDatePickerProps extends Omit<DatePickerProps<Dayjs>, 'value' | 'onChange' | 'defaultValue' | 'minDate' | 'maxDate'> {
  value?: Dayjs | string | null;
  defaultValue?: Dayjs | string | null;
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
  onChange: (value: Dayjs | null) => void;
}

export const CustomDatePicker = ({
  value,
  defaultValue,
  minDate,
  maxDate,
  onChange,
  label = null,
  format = 'DD/MM/YYYY',
  disableFuture = false,
  ...others
}: CustomDatePickerProps) => (
  <DatePicker
    value={value ? dayjs(value) : null}
    defaultValue={defaultValue ? dayjs(defaultValue) : null}
    minDate={minDate ? dayjs(minDate) : undefined}
    maxDate={maxDate ? dayjs(maxDate) : undefined}
    onChange={onChange}
    label={label}
    format={format}
    disableFuture={disableFuture}
    {...others}
  />
);

const CustomStyledDatePicker = styled(StaticDatePicker)({
  '& .MuiPickersDay-root': {
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500
  },
  '& .Mui-selected': {
    color: '#fff !important'
  }
});

interface CustomStaticDatePickerProps extends Omit<
  StaticDatePickerProps<Dayjs>,
  'value' | 'onChange' | 'defaultValue' | 'minDate' | 'maxDate'
> {
  value?: Dayjs | string | null;
  defaultValue?: Dayjs | string | null;
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
  onChange: (value: Dayjs | null) => void;
}

export const CustomStaticDatePicker = ({ value, defaultValue, minDate, maxDate, onChange, ...others }: CustomStaticDatePickerProps) => (
  <CustomStyledDatePicker
    value={value ? dayjs(value) : null}
    defaultValue={defaultValue ? dayjs(defaultValue) : null}
    minDate={minDate ? dayjs(minDate) : undefined}
    maxDate={maxDate ? dayjs(maxDate) : undefined}
    onChange={onChange}
    // slotProps={{ actionBar: { actions: [] } }}
    {...others}
  />
);

interface CustomDateTimePickerProps extends Omit<
  DateTimePickerProps<Dayjs>,
  'value' | 'onChange' | 'defaultValue' | 'minDate' | 'maxDate'
> {
  value?: Dayjs | string | null;
  defaultValue?: Dayjs | string | null;
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
  onChange: (value: Dayjs | null) => void;
}

export const CustomDateTimePicker = ({
  value,
  defaultValue,
  minDate,
  maxDate,
  onChange,
  label = null,
  format = 'DD/MM/YYYY hh:mm a',
  timeSteps = { hours: 1, minutes: 1 },
  disableFuture = false,
  ...others
}: CustomDateTimePickerProps) => (
  <DateTimePicker
    value={value ? dayjs(value) : null}
    defaultValue={defaultValue ? dayjs(defaultValue) : null}
    minDate={minDate ? dayjs(minDate) : undefined}
    maxDate={maxDate ? dayjs(maxDate) : undefined}
    onChange={onChange}
    label={label}
    format={format}
    timeSteps={timeSteps}
    disableFuture={disableFuture}
    {...others}
  />
);
